import { createSelector } from '@reduxjs/toolkit';

import type {
  DocumentoPadrao,
  DocumentosPadraoContexto,
} from '@/features/documentosPadrao/types/documentosPadraoTypes';
import { possuiFiltrosAtivos } from '@/features/documentosPadrao/utils/documentosPadraoRegras';
import type { RootState } from '@/store';

/**
 * Os selectors deixaram de filtrar em memória: visibilidade, abas, busca, filtros, contadores e
 * resumo são do SERVIDOR, sob a mesma regra de escopo. Aqui é leitura direta do state.
 *
 * `selectDocumentosPadraoVisiveis` morreu junto — era o gêmeo em memória da regra de visibilidade,
 * e regra duplicada é regra que diverge.
 */

const CONTEXTO_VAZIO: DocumentosPadraoContexto = {
  usuarioId: '',
  usuarioNome: '',
  setores: [],
  meusSetoresIds: [],
};

export const selectDocumentosPadraoItens = (state: RootState): DocumentoPadrao[] =>
  state.documentosPadrao.itens;

export const selectDocumentosPadraoStatus = (state: RootState) => state.documentosPadrao.status;

export const selectDocumentosPadraoErro = (state: RootState) => state.documentosPadrao.erro;

export const selectDocumentosPadraoSalvando = (state: RootState) => state.documentosPadrao.salvando;

export const selectDocumentosPadraoAba = (state: RootState) => state.documentosPadrao.aba;

export const selectDocumentosPadraoFiltros = (state: RootState) => state.documentosPadrao.filtros;

export const selectDocumentosPadraoPaginacao = (state: RootState) =>
  state.documentosPadrao.paginacao;

export const selectDocumentosPadraoContadores = (state: RootState) =>
  state.documentosPadrao.contadores;

export const selectDocumentosPadraoResumoValidade = (state: RootState) =>
  state.documentosPadrao.resumoValidade;

export const selectDocumentoPadraoDetalhe = (state: RootState) => state.documentosPadrao.detalhe;

export const selectDocumentoPadraoCarregandoDetalhe = (state: RootState) =>
  state.documentosPadrao.carregandoDetalhe;

export const selectDocumentoPadraoPaginaUsos = (state: RootState) =>
  state.documentosPadrao.paginaUsos;

/**
 * Referência do SERVIDOR. Sem ela o badge de validade seria pintado com o relógio do navegador, que
 * é outro fuso e outro dia — a borda do 30º dia divergiria entre a lista e a faixa do topo.
 */
export const selectDocumentosPadraoReferencia = (state: RootState): string =>
  state.documentosPadrao.referencia ?? new Date().toISOString().slice(0, 10);

export const selectDocumentosPadraoDiasAviso = (state: RootState): number | null =>
  state.documentosPadrao.diasAvisoVencimento;

export const selectDocumentosPadraoContexto = (state: RootState): DocumentosPadraoContexto =>
  state.documentosPadrao.contexto ?? CONTEXTO_VAZIO;

export const selectDocumentosPadraoPartes = (state: RootState) => state.documentosPadrao.partes;

export const selectDocumentosPadraoBuscandoPartes = (state: RootState) =>
  state.documentosPadrao.buscandoPartes;

export const selectDocumentosPadraoSetores = createSelector(
  [selectDocumentosPadraoContexto],
  (contexto) => contexto.setores,
);

export const selectDocumentosPadraoTemFiltrosAtivos = createSelector(
  [selectDocumentosPadraoFiltros],
  (filtros) => possuiFiltrosAtivos(filtros),
);
